/**
 * ATL-1354 — where OPERATIONAL telemetry goes, and why it is not Sentry.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * THE INCIDENT THIS EXISTS BECAUSE OF
 *
 * On 2026-09-18 Sentry had accepted ZERO events for nine days. Not a broken SDK and not a bad DSN:
 * the organization is on the free Developer plan — 5,000 errors/month, `allowOnDemand: false`, so
 * there is no overage to spill into — and usage stood at 5,032. Every event since had been refused,
 * 430 of them on the day it was noticed. The read cutover ramped from ~34 to 101 organizations
 * inside that window with the error channel dark.
 *
 * Three of the largest consumers were our own condition reporters — `reportRealtimeOutage` (117),
 * `reportDrainOutcome` (39), `onFlagDivergence` (18) — using the error allowance as a telemetry
 * pipe. They are the reason the allowance ran out early, and they are the reason a genuine crash
 * later in the month would have had nowhere to land.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * THE RULE
 *
 *   Sentry answers "something threw and I need the stack".      → `captureException` on a REAL error.
 *   PostHog answers "how often does this condition happen,      → `captureOperationalEvent`.
 *   to how many tills, and did it start when we shipped X?"
 *
 * A condition is not an exception. `new Error(message)` built purely to carry a measurement has no
 * stack worth keeping — it points at the reporter, never at a cause — and it costs one of the 5,000.
 * Measurements belong where they can be counted, grouped by organization and graphed over a release.
 *
 * **Sentry BREADCRUMBS stay.** They are free: a breadcrumb consumes no quota and rides along with
 * whatever real exception happens next, which is exactly when its context is worth having. Only
 * `captureException` / `captureMessage` create billable events. So the pattern is breadcrumb for
 * diagnosis, PostHog event for measurement, and Sentry events reserved for things that threw.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * NO PII, AND NO HIGH-CARDINALITY IDENTIFIERS EITHER
 *
 * Callers pass counts, durations, enum-ish kinds and booleans. Never an email, phone, cashier name
 * or raw channel name — Supabase channel names are `${table}:${organizationUuid}`, so the table is
 * the dimension worth keeping and the UUID is both needless (PostHog already groups by organization)
 * and a cardinality explosion.
 *
 * `redactPii` drops an obviously-identifying property and ships the rest — losing a measurement is
 * worse than losing one field of it — and says so loudly in development. It is a BACKSTOP for a
 * future caller, NOT the thing that makes the payloads safe: every call site today passes literal
 * keys, and a backstop that has never had anything to catch is not evidence of anything. What makes
 * a payload safe is choosing counts and kinds at the call site, which is why `channelTables()` and
 * the enumerated outbox fields exist rather than a blanket spread of whatever was in scope.
 */
import type { PostHog } from 'posthog-react-native';

/** Properties an operational event may carry: countable, groupable, never identifying. */
export type OperationalProperties = Record<string, string | number | boolean | null | string[]>;

type Sink = Pick<PostHog, 'capture'>;

let sink: Sink | null = null;

/**
 * Events captured before the sink is registered, so an app-start signal is not lost merely for
 * being early. `startDualWriteOutboxAutoDrain()` runs at app start and its stall report — the state
 * in which writes are being permanently lost — can fire before React has mounted the provider.
 *
 * Bounded, because an app that never mounts the provider must not grow this without limit. On
 * overflow the OLDEST is dropped: a stale condition from minutes ago is worth less than the current
 * one, and the drop is counted and emitted (`operational_telemetry_buffer_overflowed`) so a full
 * buffer is visible rather than silent. Overflow is close to unreachable in practice — every
 * reporter feeding this is itself throttled (the outbox stall at most hourly, the realtime ladder
 * three times per outage) — but "close to unreachable" is not a reason to drop silently.
 */
const PENDING_LIMIT = 20;
const pending: Array<{ event: string; properties: OperationalProperties }> = [];
let droppedBeforeRegistration = 0;

/** Keys whose VALUE must never leave the device, matched case-insensitively on the key. */
const PII_KEY = /email|phone|password|token|secret|firstname|lastname|fullname|address|iin|card/i;

/**
 * @returns the properties minus anything that looks like PII, and the names it removed.
 *
 * Exported for the test that proves the filter can actually fire — a redactor nobody has watched
 * remove something is indistinguishable from one that returns its input.
 */
export const redactPii = (
  properties: OperationalProperties,
): { safe: OperationalProperties; removed: string[] } => {
  const safe: OperationalProperties = {};
  const removed: string[] = [];
  for (const [key, value] of Object.entries(properties)) {
    if (PII_KEY.test(key)) {
      removed.push(key);
      continue;
    }
    safe[key] = value;
  }
  return { safe, removed };
};

/**
 * Register the PostHog client for non-React modules.
 *
 * `usePostHog()` is a hook, and the two loudest reporters (`supabaseClient`, `dualWriteOutbox`) are
 * plain modules that can never call one. Registration is how they reach it; it happens once, from a
 * component mounted inside `PostHogProvider`.
 */
export const registerOperationalTelemetrySink = (client: Sink | null | undefined): void => {
  if (!client) return;
  sink = client;
  const queued = pending.splice(0, pending.length);
  if (droppedBeforeRegistration > 0) {
    queued.push({
      event: 'operational_telemetry_buffer_overflowed',
      properties: { dropped: droppedBeforeRegistration, limit: PENDING_LIMIT },
    });
    droppedBeforeRegistration = 0;
  }
  for (const item of queued) emit(item.event, item.properties);
};

/** Test seam: forget the sink and anything buffered. */
export const resetOperationalTelemetryForTests = (): void => {
  sink = null;
  pending.length = 0;
  droppedBeforeRegistration = 0;
};

const emit = (event: string, properties: OperationalProperties): void => {
  try {
    sink?.capture?.(event, properties);
  } catch {
    // Telemetry must never be able to break the thing it is measuring. A realtime outage report
    // that throws would take down the reconnect ladder that is trying to recover from the outage.
  }
};

/**
 * Record an operational condition. Safe to call from anywhere, at any time, including before the
 * sink exists and including inside a `catch`.
 */
export const captureOperationalEvent = (
  event: string,
  properties: OperationalProperties = {},
): void => {
  const { safe, removed } = redactPii(properties);
  if (removed.length > 0) {
    safe.piiPropertiesDropped = removed.length;
    // ⛔ THIS MUST NOT THROW, and an earlier revision of this file did.
    //
    // `reportRealtimeOutage` clears `outageTimer` on entry and re-arms it AFTER capturing; a throw
    // in between escapes the setTimeout callback with the timer null and `reconcileOutageTimer()`
    // never called, which kills realtime outage reporting for the rest of the session. The same
    // shape exists in `drainDualWriteOutbox`, which would skip its interval reconciliation. A guard
    // against losing telemetry must not be able to take out the reporter it guards — the invariant
    // `emit()` below is already written to preserve.
    //
    // So: drop the offending property, ship the measurement, and be LOUD in development, where a
    // console error is enough to get it fixed and cannot break a merchant's till.
    // `typeof` rather than a bare `__DEV__`: this repo has already met a runtime where it is not
    // defined (`global/Firebase/dualWrite.ts`, `global/config/supabaseReads.ts`), and a
    // ReferenceError here would be the very failure this comment exists to prevent.
    if (typeof __DEV__ !== 'undefined' && __DEV__) {
      // eslint-disable-next-line no-console
      console.error(
        `[operationalTelemetry] event "${event}" carried PII-shaped propert(ies): ` +
          `${removed.join(', ')}. Operational telemetry takes counts and kinds, never identities.`,
      );
    }
  }
  if (!sink) {
    if (pending.length >= PENDING_LIMIT) {
      pending.shift();
      droppedBeforeRegistration += 1;
    }
    pending.push({ event, properties: safe });
    return;
  }
  emit(event, safe);
};
